import { Link } from '@tanstack/react-router'

import BrokerMark from '@/components/ui/BrokerMark'
import CardState, { type CardStateValue } from '@/components/ui/CardState'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import Skeleton from '@/components/ui/Skeleton'
import StarRating from '@/components/ui/StarRating'
import {
  TOP_RATED_LIMIT,
  topRatedBrokers,
  useBrokerDirectory,
} from '@/features/broker-rail/api/queries'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { brokerShortName, type BrokerListRow } from '@/schemas/broker'

/** One row's box: `py-2` around a 20px line, 36 (the mock's 34 on the grid). */
function TopRatedRowSkeleton() {
  return (
    <div className="flex items-center gap-2 py-2">
      <Skeleton className="size-5 rounded-full" />
      <Skeleton className="h-4 w-28 rounded-xs" />
      <Skeleton className="ml-auto h-4 w-10 rounded-xs" />
    </div>
  )
}

const SKELETON_ROWS = Array.from({ length: TOP_RATED_LIMIT }, (_, index) => (
  <TopRatedRowSkeleton key={index} />
))

/**
 * One house: the 18px code badge (the mock's `bra-rail__mark`, built 20), its short name 14/600 in
 * the primary ink, and "★ 4.4" in the rating gold at the far end. The whole row opens the house's
 * page; it takes the rail's wash and underlines the name, as the mock's does.
 */
function TopRatedRow({ row }: { row: BrokerListRow }) {
  return (
    <Link
      to="/brokers/$code"
      params={{ code: row.code }}
      title={row.name}
      className="group/rail-row flex items-center gap-2 border-b border-border-subtle py-2 rail-row-wash last:border-b-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
    >
      <BrokerMark
        brokerId={row.code}
        size={18}
        className="transition-colors group-hover/rail-row:text-foreground group-focus-visible/rail-row:text-foreground"
      />
      <span className="min-w-0 flex-1 truncate text-h-s text-foreground underline-offset-3 group-hover/rail-row:underline group-focus-visible/rail-row:underline">
        {brokerShortName(row.name)}
      </span>
      <StarRating
        value={row.rating}
        variant="compact"
        size="sm"
        className="shrink-0 text-body-m text-rating-star"
      />
    </Link>
  )
}

/**
 * The Broker rail's second block: the five best-rated houses (the mock's `bra-rail__top`), from the
 * broker directory sorted by members' ratings on the client (`topRatedBrokers`). "All reviews"
 * opens All brokers, as the mock's does.
 */
export default function TopRatedBrokersCard() {
  const block = useRailBlock('top-rated')
  const directory = useBrokerDirectory()
  const { data, isPending, isError } = directory
  const manual = useManualRefresh([directory])
  const rows = data === undefined ? [] : topRatedBrokers(data.rows)

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : rows.length === 0
        ? 'empty'
        : manual.isManual
          ? 'refreshing'
          : 'loaded'

  return (
    <section aria-label="Top rated brokers" {...block}>
      <SectionHead
        size="sm"
        title="Top rated brokers"
        action={
          <SeeAllLink to="/brokers/all" size="rail" aria-label="All brokers and their reviews">
            All reviews
          </SeeAllLink>
        }
      />
      <div className="mt-2">
        <CardState
          state={state}
          skeleton={<RailRows block="top-rated" rows={SKELETON_ROWS} />}
          onRetry={manual.refresh}
          failedTitle="Couldn't load the ratings"
          failedDescription="They will be here when the broker list answers."
          emptyTitle="No ratings yet"
          emptyDescription="Brokers show here once members rate them."
        >
          <RailRows
            block="top-rated"
            rows={rows.map((row) => (
              <TopRatedRow key={row.code} row={row} />
            ))}
          />
        </CardState>
      </div>
    </section>
  )
}
